import { Button } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ChatBubbleOutline from "@material-ui/icons/ChatBubbleOutline";
import clsx from "clsx";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import {
  Widget,
  deleteMessages,
  renderCustomComponent,
} from "react-chat-widget";
import "react-chat-widget/lib/styles.css";
import { useDispatch, useSelector } from "react-redux";
import {
  asyncGetChannelHistory,
  asyncSetReadMessages,
  handleChatOpen,
} from "../chat/actions/chatActions";
import { ChatMessage } from "../chatMessage";
import { ChatNewMessage } from "../chatNewMessage";
import { ChatOfflineMessage } from "../chatOfflineMessage";
import { ChatOldMessage } from "../chatOldMessage";

const useStyles = makeStyles((theme) => ({
  root: () => ({
    "& .rcw-widget-container": {
      height: "auto",
      width: "auto",
      [theme.breakpoints.up("sm")]: {
        height: "auto",
      },
      "& .rcw-messages-container": {
        [theme.breakpoints.only("sm")]: {
          height: "100%",
          maxHeight: "calc(100% - 100px)",
        },
      },
      "& .rcw-conversation-container": {
        [theme.breakpoints.up("sm")]: {
          maxWidth: "370px",
          marginBottom: "10px",
        },
      },
    },
    "& .rcw-conversation-container .rcw-header": {
      backgroundColor: "#4187B6 !important",
      color: "white !important",
    },
    "& .rcw-launcher": {
      backgroundColor: "#4187B6 !important",
      right: 16,
      "& .MuiButton-label": {
        color: "white !important",
      },
    },
    "& .rcw-conversation-container .rcw-title": {
      fontSize: "14px !important",
    },
    " & .rcw-client .rcw-message-text": {
      backgroundColor: "#4187B642",
    },
    " & .rcw-conversation-container .rcw-title": {
      fontSize: "20px",
      padding: "0 0",
    },
    "& .balon1": {
      float: "right",
      background: "#35cce6",
      borderRadius: "10px",
    },
    "& .invisible": {
      display: "none",
    },
    [theme.breakpoints.down("sm")]: {
      height: "auto",
      width: "auto",
      "& .rcw-widget-container": {
        padding: 0,
      },
      "&.rootActive .rcw-widget-container": {
        left: 0,
      },
      "& .rcw-conversation-container.active": {
        transform: "translateY(-16px)",
      },
      "& .rcw-conversation-container.active + button": {
        display: "none",
      },
      "& .rcw-launcher": {
        bottom: 16,
      },
      "& .rcw-conversation-container": {
        height: "100vh",
        width: "100vw !important",
        marginBottom: "0",
        maxHeight: "calc(100vh - 70px)",
        maxWidth: "calc(100vw - 16px) !important",
      },
      "& .rcw-conversation-container .rcw-close-button": {
        backgroundColor: "#4187B6 !important",
        border: 0,
        display: "block",
        position: "absolute",
        right: 10,
        top: 20,
        width: 40,
        cursor: "pointer",
      },
      "& .rcw-conversation-container .rcw-close": {
        width: 20,
        height: 20,
      },
    },
  }),
  bubble: {
    width: "auto",
    borderRadius: "100%",
  },
  unread: {
    position: "fixed",
    top: "-3px",
    right: "-3px",
    backgroundColor: "red",
    color: "#fff",
    width: "25px",
    height: "25px",
    textAlign: "center",
    lineHeight: "25px",
    borderRadius: "50%",
  },
  invisible: {
    display: "none",
  },
}));

export const ChatWidget = ({
  sendMessage,
  chat,
  channelId,
  setChat,
  isOnline,
  educatorsName,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const participants = useSelector(
    (state) => state.chatReducer.channel.participants
  );
  const { channelHistory, chatOpen } = useSelector(
    (state) => state.chatReducer
  );
  const [badge, setBadge] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [messageOff, setMessageOff] = useState(true);
  const user = useSelector((state) => state.profile);

  const getParticipant = () => {
    return participants.find((e) => e.email !== user.email);
  };

  const getTitle = () => {
    return getParticipant()?.email;
  };

  const handleNewUserMessage = (newMessage) => {
    const to = getParticipant();
    if (newMessage) {
      sendMessage(to.profileId, newMessage);
    }
    if (!isOnline && messageOff) {
      renderCustomComponent(
        ChatOfflineMessage,
        {
          message: `${educatorsName} is currently offline, he'll reply once he get's connected.`,
        },
        false
      );
      setMessageOff(false);
    }
  };

  const handleOnOpen = () => {
    setIsOpen((prev) => !prev);
    setBadge(0);
    dispatch(handleChatOpen(!chatOpen));
    dispatch(asyncSetReadMessages(channelId));
    if (!isOpen) {
      dispatch(asyncGetChannelHistory(channelId));
    }
  };

  useEffect(() => {
    dispatch(asyncGetChannelHistory(channelId));
    deleteMessages();
    let unread = 0;
    channelHistory.forEach((message) => {
      const time = new Date(message.sendAt).toLocaleString();
      if (message.from !== user.participantProfileId) {
        if (message.isRead === false) {
          unread += 1;
          renderCustomComponent(
            ChatNewMessage,
            { message: message.body, time },
            false
          );
        } else {
          renderCustomComponent(
            ChatOldMessage,
            { message: message.body, time },
            false
          );
        }
      } else {
        renderCustomComponent(
          ChatMessage,
          {
            message: message.body,
            time,
          },
          false
        );
      }
    });
    setBadge(unread);
  }, [channelHistory.length, badge]);

  useEffect(() => {
    if (chat.length > 0) {
      let unread = 0;
      chat.forEach((message) => {
        const time = new Date().toLocaleTimeString("en-US");
        if (!message.isRead) {
          unread += 1;
          renderCustomComponent(
            ChatNewMessage,
            { message: message.body, time },
            false
          );
        } else {
          renderCustomComponent(
            ChatOldMessage,
            { message: message.body, time },
            false
          );
        }
      });
      setChat([]);
      setBadge(unread);
    }
  }, [chat.length, badge]);

  useEffect(() => {
    const offline = document.getElementsByClassName("offline-msg");
    if (isOnline && offline.length > 0) {
      offline[0].className = "invisible";
      setMessageOff(true);
    }
  }, [isOnline]);

  const getCustomLauncher = (handleToggle) => (
    <Button
      className={`rcw-launcher ${classes.bubble}`}
      type="button"
      fullWidth
      variant="contained"
      color="primary"
      onClick={(e) => {
        e.preventDefault();
        handleOnOpen();
        handleToggle();
        setTimeout(() => {
          const closeButton =
            window.document.getElementsByClassName("rcw-close-button");
          if (closeButton && closeButton[0]) {
            closeButton[0].addEventListener("click", handleOnOpen);
          }
        }, 1000);
      }}
    >
      <ChatBubbleOutline style={{ fontSize: 35 }} />
      {badge ? <span className={classes.unread} /> : null}
    </Button>
  );

  return (
    <div className={clsx(classes.root, { rootActive: isOpen })}>
      <Widget
        handleNewUserMessage={handleNewUserMessage}
        profileAvatar="/no_profile.png"
        titleAvatar="/no_profile.png"
        title={getTitle()}
        subtitle={isOnline ? "online" : "offline"}
        showAvatar
        launcher={(handleToggle) => getCustomLauncher(handleToggle)}
      />
    </div>
  );
};
ChatWidget.propTypes = {
  sendMessage: PropTypes.func.isRequired,
  chat: PropTypes.arrayOf(),
  channelId: PropTypes.string.isRequired,
  setChat: PropTypes.func.isRequired,
  isOnline: PropTypes.bool,
  educatorsName: PropTypes.string,
};
ChatWidget.defaultProps = {
  chat: [],
  isOnline: false,
  educatorsName: "",
};
